import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterCarCategory from "./RegisterCarCategory";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";
// import CarModelList from "./CarModelList";

type DataIndex = keyof Types.CarCategoryDto;

function CarCategoryList() {
    const alert = useAlert();

    const [carCategories, setCarCategories] = useState<Array<Types.CarCategoryDto>>();
    const [currentCarCategory, setcurrentCarCategory] = useState<Types.CarCategoryDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteCarCategory, setOpenDeleteCarCategory] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.CarCategoryDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.CarCategoryDto> = [
        {
            align: "center",
            title: String.carCategoryId,
            dataIndex: "categoryId",
            key: "categoryId",
            ...getColumnSearchProps("categoryId"),
        },
        {
            align: "center",
            title: String.carCategoryName,
            dataIndex: "categoryName",
            key: "categoryName",
            ...getColumnSearchProps("categoryName"),
        },
        {
            align: "center",
            title: String.unavailable,
            dataIndex: "unavailable",
            key: "unavailable",
            ...getColumnSearchProps("unavailable"),
        },
    ];

    const requestAxiosGetCarCategories = async () => {
        const response = await utilAxiosWithAuth().get(Request.CAR_CATEGORY_LIST_URL);
        // console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetCarCategories,
        error: errorGetCarCategories,
        data: resultGetCarCategories,
        execute: requestGetCarCategories,
    } = useAsyncAxios(requestAxiosGetCarCategories);

    useEffect(() => {
        if (resultGetCarCategories === null) return;
        // console.log("resultGetCarCategories", resultGetCarCategories);

        setCarCategories(resultGetCarCategories.carCategories);
    }, [resultGetCarCategories]);

    useEffect(() => {
        if (errorGetCarCategories === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetCarCategories.response.data.code, "Error", errorGetCarCategories.response.data.message);
    }, [errorGetCarCategories]);

    const requestAxiosRemoveCarCategory = async (carCategory: Types.CarCategoryDto) => {
        const response = await utilAxiosWithAuth().delete(Request.CAR_CATEGORY_URL + "/" + carCategory?.categoryId);
        return response.data;
    };

    const {
        loading: loadingRemoveCarCategory,
        error: errorRemoveCarCategory,
        data: resultRemoveCarCategory,
        execute: requestRemoveCarCategories,
    } = useAsyncAxios(requestAxiosRemoveCarCategory);

    useEffect(() => {
        if (resultRemoveCarCategory === null) return;
        // console.log("resultRemoveCarCategory", resultRemoveCarCategory);

        alert.setAlert(AlertType.SUCCESS, "카테고리 삭제 성공", "카테고리 삭제를 완료하였습니다.");

        requestGetCarCategories();
    }, [resultRemoveCarCategory]);

    useEffect(() => {
        if (errorRemoveCarCategory === null) return;

        alert.setAlert(errorRemoveCarCategory.response.data.code, "Error", errorRemoveCarCategory.response.data.message);
    }, [errorGetCarCategories]);

    useEffect(() => {
        requestGetCarCategories();
    }, []);

    const onRow = (value: Types.CarCategoryDto) => {
        //console.log("onRow", value);
        setcurrentCarCategory(value);
    };

    const handleDeleteCarCategory = () => {
        if (currentCarCategory !== undefined) setOpenDeleteCarCategory(true);
    };

    const handleDeleteCarCategoryOk = () => {
        setOpenDeleteCarCategory(false);
        requestRemoveCarCategories(currentCarCategory);
    };

    const handleDeleteCarCategoryCancel = () => {
        setOpenDeleteCarCategory(false);
    };

    const onRegisterCarCategory = () => {
        requestGetCarCategories();
        setEditMode("none");
    };

    return (
        <>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Row justify={"space-between"}>
                    <Col span={6}>
                        <Divider orientation="left">
                            <span>분류</span>
                        </Divider>
                    </Col>
                    <Space>
                        <Button
                            onClick={() => {
                                editMode === "add" ? setEditMode("none") : setEditMode("add");
                            }}
                        >
                            {String.userAdd}
                        </Button>
                        <Button
                            onClick={() => {
                                editMode === "update" ? setEditMode("none") : setEditMode("update");
                            }}
                        >
                            {String.userUpdate}
                        </Button>
                        <Button onClick={() => handleDeleteCarCategory()} danger>
                            {String.userRemove}
                        </Button>
                    </Space>
                </Row>
            </Space>
            <DataTable hasScroll={false} columns={columns} rowKey="categoryId" dataSource={carCategories} onRow={onRow} />

            {editMode === "add" && <RegisterCarCategory editMode={editMode} onRegisterCarCategory={onRegisterCarCategory} />}

            {editMode === "update" && currentCarCategory !== undefined && (
                <RegisterCarCategory editMode={editMode} carCategory={currentCarCategory} onRegisterCarCategory={onRegisterCarCategory} />
            )}

            {openDeleteCarCategory && (
                <Modal open={openDeleteCarCategory} onOk={handleDeleteCarCategoryOk} onCancel={handleDeleteCarCategoryCancel}>
                    {currentCarCategory?.categoryName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default CarCategoryList;
